import React, { useEffect, useMemo, useState } from "react";
import { fetchReviews, fetchReviewsSummary, fetchProducts } from "../lib/api";
import { ReviewCard } from "../components/ReviewCard";
import { ReviewForm } from "../components/ReviewForm";
import { StarRating } from "../components/StarRating";
import { Seo } from "../components/Seo";

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [products, setProducts] = useState([]);
  const [summary, setSummary] = useState({ overall: { count: 0, avg: 0 } });
  const [productFilter, setProductFilter] = useState("all");

  const load = async (product) => {
    const list = await fetchReviews(product && product !== "all" ? product : undefined);
    setReviews(list);
  };

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => {});
    fetchReviewsSummary().then(setSummary).catch(() => {});
    load("all");
  }, []);

  useEffect(() => { load(productFilter); }, [productFilter]);

  const productLookup = useMemo(() => {
    const map = {};
    for (const p of products) map[p.slug] = p;
    return map;
  }, [products]);

  const reviewableProducts = useMemo(
    () => products.filter((p) => (summary.per_product || {})[p.slug]),
    [products, summary],
  );

  const onSubmitted = (created) => {
    setReviews((prev) => [created, ...prev]);
    // Refresh summary after a new review
    fetchReviewsSummary().then(setSummary).catch(() => {});
  };

  return (
    <div data-testid="reviews-page" className="max-w-[1360px] mx-auto px-6 lg:px-10 py-16">
      <Seo title="Customer Reviews" description="Read verified researcher reviews of Peptide Labs N.I. peptides including BPC-157, NAD+, MOTS-c and our research bundles." />
      {/* Header */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <div className="label-mono">Reviews</div>
          <h1 className="mt-4 text-4xl lg:text-6xl tracking-tight text-white font-medium">
            Verified researcher reviews.
          </h1>
          <p className="mt-4 text-[#B7BCC7] max-w-xl">
            Feedback focused on what matters for lab work — purity, packaging, documentation,
            and cold-chain fulfilment. Never on efficacy or health outcomes.
          </p>
        </div>
        <div className="lg:col-span-4">
          <div className="p-6 border border-[#2A2A2A] rounded-3xl bg-[#0e0e0e]">
            <div className="label-mono">Overall rating</div>
            <div className="mt-4 flex items-baseline gap-3">
              <span className="text-white text-5xl font-medium font-mono tabular-nums" data-testid="reviews-overall-avg">
                {summary.overall.avg.toFixed(1)}
              </span>
              <span className="text-[#6E7784] text-lg font-mono">/ 5</span>
            </div>
            <div className="mt-3"><StarRating value={summary.overall.avg} size={16} /></div>
            <div className="mt-4 text-[12px] font-mono text-[#6E7784]" data-testid="reviews-overall-count">
              Based on {summary.overall.count} verified reviews
            </div>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="mt-12 flex flex-wrap gap-2" data-testid="reviews-filter-bar">
        <button
          onClick={() => setProductFilter("all")}
          data-testid="reviews-filter-all"
          className={`px-4 py-2 rounded-full text-sm border transition-colors ${
            productFilter === "all"
              ? "bg-[#1565FF] text-white border-[#1565FF]"
              : "border-[#2A2A2A] text-[#B7BCC7] hover:border-[#1565FF] hover:text-white"
          }`}
        >
          All ({summary.overall.count})
        </button>
        {reviewableProducts.map((p) => {
          const s = (summary.per_product || {})[p.slug];
          return (
            <button
              key={p.slug}
              onClick={() => setProductFilter(p.slug)}
              data-testid={`reviews-filter-${p.slug}`}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                productFilter === p.slug
                  ? "bg-[#1565FF] text-white border-[#1565FF]"
                  : "border-[#2A2A2A] text-[#B7BCC7] hover:border-[#1565FF] hover:text-white"
              }`}
            >
              {p.name} · {s?.count || 0}
            </button>
          );
        })}
      </div>

      {/* Grid */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reviews.map((r) => (
          <ReviewCard key={r.id} review={r} showProductLink productLookup={productLookup} />
        ))}
      </div>

      {reviews.length === 0 && (
        <div className="mt-8 border border-[#2A2A2A] rounded-3xl bg-[#0e0e0e] p-12 text-center text-[#6E7784]">
          No reviews yet for this product. Be the first to leave one.
        </div>
      )}

      {/* Form */}
      <div className="mt-16">
        <ReviewForm
          productSlug={productFilter === "all" ? null : productFilter}
          onSubmitted={onSubmitted}
        />
      </div>
    </div>
  );
}
